import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import i18n from '@/i18n'
import {
  DEFAULT_LANGUAGE,
  LOCALE_STORAGE_KEY,
  isAppLanguage,
  type AppLanguage,
} from '@/i18n/types'
import { authService } from '@/services/authService'

type LocaleContextValue = {
  language: AppLanguage
  setLanguage: (language: AppLanguage) => Promise<void>
  syncFromUser: (preferredLanguage?: string | null) => void
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined)

function applyDocumentLanguage(language: AppLanguage): void {
  document.documentElement.lang = language
}

function readStoredLanguage(): AppLanguage {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (isAppLanguage(stored)) {
    return stored
  }
  return DEFAULT_LANGUAGE
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => readStoredLanguage())

  useEffect(() => {
    const initial = readStoredLanguage()
    void i18n.changeLanguage(initial)
    applyDocumentLanguage(initial)
    setLanguageState(initial)
  }, [])

  const syncFromUser = useCallback((preferredLanguage?: string | null) => {
    if (!isAppLanguage(preferredLanguage)) {
      return
    }
    void i18n.changeLanguage(preferredLanguage)
    localStorage.setItem(LOCALE_STORAGE_KEY, preferredLanguage)
    applyDocumentLanguage(preferredLanguage)
    setLanguageState(preferredLanguage)
  }, [])

  const setLanguage = useCallback(async (next: AppLanguage) => {
    await i18n.changeLanguage(next)
    localStorage.setItem(LOCALE_STORAGE_KEY, next)
    applyDocumentLanguage(next)
    setLanguageState(next)

    const accessToken = authService.getAccessToken()
    if (accessToken && !authService.isTokenExpired(accessToken)) {
      try {
        await authService.patchProfile({ preferred_language: next })
      } catch {
        /* preference saved locally; server sync is best-effort */
      }
    }
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({ language, setLanguage, syncFromUser }),
    [language, setLanguage, syncFromUser],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}
